import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import useDynamicBottom from "../../../hooks/useDynamicBottom";
import "../../../styles/styles.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const MAX_NAME_LENGTH = 40;
const MAX_EMAIL_LENGTH = 100;
const MAX_COMMENTS_LENGTH = 500;
const MAX_PHONE_DIGITS = 10;

const onlyNameChars = (value: string) =>
  value.replace(/[^a-zA-Z\s'-]/g, "").slice(0, MAX_NAME_LENGTH);

const onlyEmailChars = (value: string) =>
  value.replace(/\s/g, "").slice(0, MAX_EMAIL_LENGTH);

const onlyPhoneDigits = (value: string) =>
  value.replace(/\D/g, "").slice(0, MAX_PHONE_DIGITS);

const formatUSPhone = (digits: string) => {
  const cleaned = onlyPhoneDigits(digits);

  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6)
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

const isValidEmail = (email: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const ContactForm: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [submitMessage, setSubmitMessage] = useState("");

  const [formData, setFormData] = useState({
    help: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    comments: "",
  });

  const [errors, setErrors] = useState({
    help: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    comments: "",
  });

  const { bottom } = useDynamicBottom(10, "1rem", isOpen);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const resetForm = () => {
    setFormData({
      help: "",
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      comments: "",
    });

    setErrors({
      help: "",
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      comments: "",
    });

    setSubmitStatus("idle");
    setSubmitMessage("");
  };

  const toggleForm = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (!next) resetForm();
      return next;
    });
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

    let nextValue = value;

    if (name === "firstName" || name === "lastName") {
      nextValue = onlyNameChars(value);
    }

    if (name === "phone") {
      const digits = onlyPhoneDigits(value);
      nextValue = formatUSPhone(digits);
    }

    if (name === "email") {
      nextValue = onlyEmailChars(value);
    }

    if (name === "comments") {
      nextValue = value.slice(0, MAX_COMMENTS_LENGTH);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    if (submitStatus !== "idle") {
      setSubmitStatus("idle");
      setSubmitMessage("");
    }
  };

  const validateForm = () => {
    const phoneDigits = onlyPhoneDigits(formData.phone);

    const newErrors = {
      help: "",
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      comments: "",
    };

    if (!formData.help.trim()) {
      newErrors.help = "This field is required.";
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required.";
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = "First name is too short.";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required.";
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = "Last name is too short.";
    }

    if (!phoneDigits) {
      newErrors.phone = "Phone number is required.";
    } else if (phoneDigits.length !== 10) {
      newErrors.phone = "Enter a valid 10-digit US phone number.";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email address is required.";
    } else if (!isValidEmail(formData.email.trim())) {
      newErrors.email = "Enter a valid email address.";
    }

    if (!formData.comments.trim()) {
      newErrors.comments = "Comments are required.";
    } else if (formData.comments.trim().length < 5) {
      newErrors.comments = "Comments are too short.";
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setSubmitStatus("idle");
    setSubmitMessage("");

    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      const payload = {
        ...formData,
        phone: onlyPhoneDigits(formData.phone),
        email: formData.email.trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        comments: formData.comments.trim(),
      };

      const response = await axios.post(`${API_URL}/api/send-email`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("Email sent:", response.data);

      setSubmitStatus("success");
      setSubmitMessage("Your message has been sent successfully.");

      setFormData({
        help: "",
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        comments: "",
      });

      setErrors({
        help: "",
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        comments: "",
      });
    } catch (error: any) {
      console.error(
        "Email send error:",
        error?.response?.data || error?.message || error,
      );

      setSubmitStatus("error");
      setSubmitMessage(
        "There was an error sending your message. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        resetForm();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  return (
    <div style={{ position: "fixed", bottom, left: "1rem", zIndex: 50 }}>
      <button className="button-common fixed-button" onClick={toggleForm}>
        <span className="text-xs sm:text-sm">Talk to Us</span>
        <FontAwesomeIcon
          icon={isOpen ? faChevronDown : faChevronUp}
          className="ml-2"
        />
      </button>

      {isOpen && (
        <form
          ref={formRef}
          className="form-common w-full sm:w-auto"
          onSubmit={handleSubmit}
        >
          <h2 className="text-lg font-bold mb-2 sm:mb-4">How can we help?</h2>

          {submitStatus !== "idle" && (
            <div
              style={{
                marginBottom: "12px",
                padding: "10px 12px",
                borderRadius: "8px",
                fontSize: "14px",
                lineHeight: 1.4,
                backgroundColor:
                  submitStatus === "success"
                    ? "rgba(34,197,94,0.12)"
                    : "rgba(239,68,68,0.12)",
                color: submitStatus === "success" ? "#166534" : "#991b1b",
                border:
                  submitStatus === "success"
                    ? "1px solid rgba(34,197,94,0.28)"
                    : "1px solid rgba(239,68,68,0.28)",
              }}
            >
              {submitMessage}
            </div>
          )}

          <div className="mb-2 sm:mb-3">
            <select
              name="help"
              value={formData.help}
              onChange={handleChange}
              className="input-common"
            >
              <option value="">Select an option...</option>
              <option value="I need sales assistance">
                I need sales assistance
              </option>
              <option value="I am currently a client and need assistance">
                I am currently a client and need assistance
              </option>
              <option value="I am currently an employee">
                I am currently an employee
              </option>
              <option value="I would like to become an employee">
                I would like to become an employee
              </option>
            </select>
            {errors.help && <p className="error-text">{errors.help}</p>}
          </div>

          <div className="mb-2 sm:mb-3">
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="First Name"
              className="input-common"
              maxLength={MAX_NAME_LENGTH}
            />
            {errors.firstName && (
              <p className="error-text">{errors.firstName}</p>
            )}
          </div>

          <div className="mb-2 sm:mb-3">
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Last Name"
              className="input-common"
              maxLength={MAX_NAME_LENGTH}
            />
            {errors.lastName && <p className="error-text">{errors.lastName}</p>}
          </div>

          <div className="mb-2 sm:mb-3">
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(555) 555-5555"
              className="input-common"
              maxLength={14}
            />
            {errors.phone && <p className="error-text">{errors.phone}</p>}
          </div>

          <div className="mb-2 sm:mb-3">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email Address"
              className="input-common"
              maxLength={MAX_EMAIL_LENGTH}
            />
            {errors.email && <p className="error-text">{errors.email}</p>}
          </div>

          <div className="mb-2 sm:mb-3">
            <textarea
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              placeholder="Comments"
              className="input-common h-20"
              maxLength={MAX_COMMENTS_LENGTH}
            ></textarea>
            <div style={{ fontSize: "12px", marginTop: "4px", opacity: 0.8 }}>
              {formData.comments.length}/{MAX_COMMENTS_LENGTH}
            </div>
            {errors.comments && <p className="error-text">{errors.comments}</p>}
          </div>

          <button
            type="submit"
            className="button-common"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Sending..." : "Send"}
          </button>
        </form>
      )}
    </div>
  );
};

export default ContactForm;
